import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useReveal from '@/hooks/useReveal';
import { submitContactInquiry } from '@/lib/api';

export default function ContactPage() {
  useReveal();
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const honey = fd.get('phone_alt')?.toString().trim();
    if (honey) {
      setStatus('success');
      return;
    }

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setErrMsg('Please fill in all required fields.');
      return;
    }

    setStatus('submitting');
    setErrMsg('');

    const res = await submitContactInquiry(form);

    if (res.success) {
      setStatus('success');
      setForm({ name: '', company: '', email: '', phone: '', message: '' });
    } else {
      setStatus('error');
      setErrMsg(res.error || 'Unable to submit inquiry. Please try again or email us directly.');
    }
  };

  return (
    <div className="bg-ink-950 text-white min-h-screen">
      <Navbar />

      {/* HERO SECTION */}
      <section className="relative pt-[clamp(105px,8.5vw,135px)] pb-12 sm:pb-16 overflow-hidden w-full">
        <img
          src="https://readdy.ai/api/search-image?query=3D%20rendered%20abstract%20holographic%20communication%20scene%20with%20floating%20translucent%20chat%20bubbles%20and%20glowing%20purple%20neon%20connection%20nodes%20in%20deep%20dark%20space%2C%20soft%20violet%20and%20cyan%20haze%2C%20glossy%20isometric%20render%20style%2C%20floating%20holographic%20envelope%20and%20message%20icons%2C%20premium%20cinematic%20atmosphere&width=1800&height=1200&seq=fc-ct2-hero&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/80 via-ink-950/70 to-ink-950" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8 w-full">
          <div className="max-w-3xl">
            <div className="text-xs font-mono tracking-widest text-neon-400">// CONTACT US</div>
            <h1 className="mt-4 font-display text-4xl sm:text-6xl md:text-7xl font-bold leading-[1.08] tracking-tight text-gradient">
              Start Your<br />Project
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              Tell us what you are building. We will get back within 24 hours with a clear plan and quote.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT INFO + SIMPLIFIED FORM */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="mx-auto max-w-7xl grid md:grid-cols-5 gap-10 md:gap-14">
          <div className="md:col-span-2 reveal space-y-8">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Let's Talk</h2>
              <p className="mt-3 text-white/60 leading-relaxed text-sm sm:text-base">
                Whether you have a detailed brief or just an idea, we are here to help you turn it into reality.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg glass text-neon-300 shrink-0">
                  <i className="ri-mail-line text-lg" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-white/50 uppercase tracking-wider">Primary Email</div>
                  <a href="mailto:formacodetech@gmail.com" className="text-sm font-medium text-white hover:text-neon-300 transition block mt-0.5">
                    formacodetech@gmail.com
                  </a>
                  <a href="mailto:info@formacodetech.com" className="text-xs text-white/60 hover:text-neon-300 transition block mt-0.5">
                    info@formacodetech.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg glass text-neon-300 shrink-0">
                  <i className="ri-whatsapp-line text-lg" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-white/50 uppercase tracking-wider">WhatsApp / Phone</div>
                  <div className="text-sm font-medium text-white mt-0.5">03706620393</div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-3">Follow Us</h4>
              <div className="flex gap-3">
                {['linkedin-fill', 'instagram-line'].map((i) => (
                  <a key={i} href="#" className="w-10 h-10 flex items-center justify-center rounded-lg glass text-white/70 hover:text-neon-300 transition">
                    <i className={`ri-${i} text-lg`} />
                  </a>
                ))}
              </div>
            </div>

            <div className="relative pt-4 hidden md:block">
              <div className="absolute -inset-4 rounded-3xl bg-neon-500/15 blur-3xl" />
              <img
                src="https://readdy.ai/api/search-image?query=3D%20rendered%20cute%20friendly%20robot%20character%20with%20headphones%20holding%20a%20glowing%20holographic%20message%20bubble%20floating%20in%20deep%20dark%20purple%20space%2C%20translucent%20chat%20icons%20and%20connection%20lines%20orbiting%20around%2C%20soft%20purple%20and%20blue%20neon%20glow%2C%20glossy%20isometric%20render%20style%2C%20premium%20technology%20aesthetic%2C%20clean%20composition&width=900&height=700&seq=fc-ct2-side&orientation=portrait"
                alt="Let's talk"
                className="relative rounded-2xl w-full max-h-72 object-cover object-top border border-white/10"
              />
            </div>
          </div>

          <div className="md:col-span-3 reveal">
            {status === 'success' ? (
              <div className="glass rounded-2xl p-8 sm:p-12 text-center border border-white/10 shadow-2xl">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-gradient-to-br from-neon-600 to-neon-400 text-white text-3xl shadow-[0_0_30px_rgba(139,92,246,0.5)]">
                  <i className="ri-check-line" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold text-white">Message Sent!</h3>
                <p className="mt-3 text-white/60 text-sm sm:text-base leading-relaxed">
                  We have received your project inquiry and will reach out to your email within 24 hours.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 btn-primary text-white text-sm font-semibold px-6 py-3 rounded-lg inline-flex items-center gap-2"
                >
                  Send another inquiry <i className="ri-refresh-line" />
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="glass rounded-2xl p-6 sm:p-8 space-y-5 border border-white/10 shadow-2xl">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-white/80 mb-2">Name *</label>
                    <input
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                      placeholder="Your Full Name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/80 mb-2">Company</label>
                    <input
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                      placeholder="Your Company Name"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-white/80 mb-2">Email *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/80 mb-2">Contact Number</label>
                    <input
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-2">Description / Message *</label>
                  <textarea
                    name="message"
                    required
                    value={form.message}
                    onChange={handleChange}
                    maxLength={1000}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition resize-none"
                    placeholder="Tell us about your project, goals, timeline, and requirements..."
                  />
                  <div className="mt-1 text-xs text-white/30 text-right">{form.message.length}/1000</div>
                </div>

                {errMsg && (
                  <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
                    <i className="ri-error-warning-line text-lg shrink-0" />
                    <span>{errMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="btn-primary w-full text-white font-semibold px-8 py-4 rounded-lg text-sm whitespace-nowrap inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg" /> Sending...
                    </>
                  ) : (
                    <>
                      Send Message <i className="ri-send-plane-line" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}